import { useState } from "react";
import { UploadCloud, PlayCircle, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

export default function AdminImport() {
  const [raw, setRaw] = useState("");
  const [mode, setMode] = useState("upsert");
  const [report, setReport] = useState(null);
  const [validated, setValidated] = useState(false);
  const [busy, setBusy] = useState(false);

  function parseItems() {
    let parsed;
    try { parsed = JSON.parse(raw); }
    catch (e) { toast.error("Invalid JSON: " + e.message); return null; }
    const items = Array.isArray(parsed) ? parsed : parsed.items;
    if (!Array.isArray(items)) { toast.error("JSON must be an array or { items: [...] }"); return null; }
    return items;
  }

  async function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setRaw(await file.text());
    setValidated(false); setReport(null);
  }

  async function run(dry_run) {
    const items = parseItems();
    if (!items) return;
    setBusy(true);
    try {
      const { data } = await api.post("/api/admin/import", { items, dry_run, mode });
      setReport(data);
      if (dry_run) {
        setValidated(data.invalid === 0);
        toast[data.invalid === 0 ? "success" : "error"](
          data.invalid === 0 ? `Validation passed (${data.valid} valid)` : `${data.invalid} item(s) invalid`
        );
      } else {
        setValidated(false);
        toast.success(`Imported: ${data.created} created, ${data.updated} updated, ${data.skipped} skipped`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import request failed");
    } finally { setBusy(false); }
  }

  const stat = (label, value, cls = "text-slate-900") => (
    <div className="rounded-xl border border-slate-200 bg-white p-3 text-center">
      <div className={`font-heading text-2xl font-bold ${cls}`}>{value}</div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  );

  return (
    <div className="mx-auto max-w-4xl">
      <h1 className="font-heading text-2xl font-extrabold text-slate-900">Import content</h1>
      <p className="mt-1 text-sm text-slate-500">
        Paste or upload structured JSON. Items are idempotent by <code className="rounded bg-slate-100 px-1">external_key</code>.
        Always dry-run first; publishing via import is admin-only.
      </p>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="import-file-label">
            <UploadCloud className="h-4 w-4" /> Upload JSON
            <input type="file" accept="application/json,.json" onChange={onFile} className="hidden" data-testid="import-file-input" />
          </label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Mode</span>
            <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="import-mode"
              className="rounded-md border border-slate-200 px-3 py-1.5 text-sm">
              <option value="upsert">upsert (create or update)</option>
              <option value="create_only">create_only (skip existing)</option>
            </select>
          </div>
        </div>
        <Textarea value={raw} onChange={(e) => { setRaw(e.target.value); setValidated(false); }}
          rows={12} placeholder='{ "items": [ { "external_key": "...", "type": "article", ... } ] }'
          className="mt-4 font-mono text-xs" data-testid="import-json" />
        <div className="mt-4 flex flex-wrap gap-3">
          <Button onClick={() => run(true)} disabled={busy || !raw.trim()} variant="outline"
            className="rounded-full" data-testid="import-dryrun-btn">
            <PlayCircle className="mr-1.5 h-4 w-4" />{busy ? "Validating…" : "Dry run / validate"}
          </Button>
          <Button onClick={() => run(false)} disabled={busy || !validated}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="import-commit-btn">
            <CheckCircle2 className="mr-1.5 h-4 w-4" />Commit import
          </Button>
        </div>
      </div>

      {report && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="import-report">
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
            {stat("Total", report.total)}
            {stat("Valid", report.valid, "text-emerald-600")}
            {stat("Invalid", report.invalid, report.invalid ? "text-red-600" : "text-slate-900")}
            {stat("Created", report.created, "text-emerald-600")}
            {stat("Updated", report.updated, "text-blue-600")}
            {stat("Skipped", report.skipped, "text-slate-500")}
          </div>

          {report.errors?.length > 0 && (
            <div className="mt-5" data-testid="import-errors">
              <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-red-700"><XCircle className="h-4 w-4" />Errors</h3>
              <ul className="mt-2 space-y-2 text-sm">
                {report.errors.map((e, i) => (
                  <li key={i} className="rounded-lg border border-red-100 bg-red-50 p-3">
                    <div className="font-semibold text-red-800">#{e.index} {e.external_key || "(no key)"}</div>
                    <ul className="mt-1 list-disc pl-5 text-red-700">{e.errors.map((m, j) => <li key={j}>{m}</li>)}</ul>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {report.warnings?.length > 0 && (
            <div className="mt-5" data-testid="import-warnings">
              <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-amber-700"><AlertTriangle className="h-4 w-4" />{report.dry_run ? "Health-claim warnings (review before commit)" : "Health-claim warnings (flagged)"}</h3>
              <ul className="mt-2 space-y-1.5 text-sm">
                {report.warnings.map((w, i) => (
                  <li key={i} className="rounded-lg border border-amber-100 bg-amber-50 p-2.5 text-amber-800">
                    <span className="font-semibold">#{w.index} {w.external_key} [{w.locale}]</span> — {w.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {report.invalid === 0 && !report.dry_run && (
            <p className="mt-4 flex items-center gap-2 text-sm font-semibold text-emerald-700"><CheckCircle2 className="h-4 w-4" />Import committed successfully.</p>
          )}
        </div>
      )}
    </div>
  );
}
